import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, onAuthStateChanged, signInWithPopup, type User } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { CloudApiClient, DemoApiClient, type ApiClient } from './api-client';
import { FirebaseRepository } from './firebase-repository';
import { LocalRepository } from './local-repository';
import type { AppRepository } from './repository';

export interface RepositorySession { repository: AppRepository; api:ApiClient; userLabel:string; }

export class AuthRequiredError extends Error {
  constructor(){ super('Sign in with Google to continue.'); this.name='AuthRequiredError'; }
}

async function currentUser(auth:ReturnType<typeof getAuth>):Promise<User|null>{
  if(auth.currentUser)return auth.currentUser;
  return new Promise(resolve=>{const unsub=onAuthStateChanged(auth,user=>{unsub();resolve(user);});});
}

function configuredApp(){
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  if(!projectId)return null;
  if(getApps().length)return getApp();
  return initializeApp({
    apiKey:import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain:import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId,
    storageBucket:import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId:import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId:import.meta.env.VITE_FIREBASE_APP_ID,
  });
}

export async function createRepository(interactiveSignIn=false):Promise<RepositorySession> {
  const app=configuredApp();
  if(!app) return { repository:new LocalRepository(), api:new DemoApiClient(), userLabel:'Demo mode' };

  const auth = getAuth(app);
  let user:User|null;
  if(interactiveSignIn){
    // Called directly from the user's click so the browser is allowed to open Google's popup.
    user = auth.currentUser ?? (await signInWithPopup(auth,new GoogleAuthProvider())).user;
  } else {
    user = await currentUser(auth);
    if(!user)throw new AuthRequiredError();
  }

  const allowed=String(import.meta.env.VITE_ALLOWED_USER_EMAIL||'').toLowerCase();
  if(allowed&&String(user.email||'').toLowerCase()!==allowed) throw new Error('This Google account is not allowed to use Client Outreach Tool.');
  const apiUrl=String(import.meta.env.VITE_API_URL||'').replace(/\/$/,'');
  if(!apiUrl) throw new Error('VITE_API_URL is required when Firebase is enabled.');
  const api=new CloudApiClient(apiUrl,()=>user!.getIdToken());
  await api.bootstrap();
  return { repository:new FirebaseRepository(getFirestore(app),user.uid), api, userLabel:user.email || 'Connected' };
}
